import { useGetSettingsGeneral } from '@agos/api-client/platform'
import { generalNotice } from '@agos/shared'
import { serviceExplanation, serviceHeading, serviceStateOf } from '@agos/customer-menu'
import { AlertTriangle } from 'lucide-react'
import { type ReactNode } from 'react'

/**
 * Whether the shop is usable at all, and anything it wants to say — FRONTEND.md §9.187.
 *
 * **The gate was on `HomePage` and nowhere else**, so `/checkout`, `/moj-profil`, a CMS page and
 * every landing page stayed usable with the site in maintenance. `customer-home.spec.ts` proves the
 * home page closes, which is exactly why this looked finished. It lives above the router now, which
 * is where §9.3's own note says the banners belong *"so they appear on every route rather than only
 * on the home page (FB-C13)"* — the same mistake, made twice.
 *
 * **And the general notice had no reader at all.** Two settings screens each write one and the server
 * publishes both on endpoints this app already calls; nothing drew either. The *Všeobecné* one is
 * drawn here; the *Otváracie hodiny* one is drawn by `HomePage` and **only** there - §9.328, and see
 * the note at `notices` below.
 *
 * **Sixty seconds.** `useGetSettingsGeneral` had no interval, so a customer with the page already
 * open never learned the service had been switched off.
 *
 * **Failing open throughout**, which is the existing rule: a settings call that did not answer must
 * not close a restaurant that is trading, and must not invent a notice.
 */
export function ServiceGate({ children }: { children: ReactNode }) {
  const { data: general } = useGetSettingsGeneral({ query: { refetchInterval: 60_000 } })

  const service = serviceStateOf(general)
  if (service !== 'open') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center gap-6"
        data-service-state={service}
      >
        <div className="text-6xl">{service === 'maintenance' ? '🔧' : '🚫'}</div>
        <h1 className="text-2xl font-bold text-foreground">{serviceHeading(service)}</h1>
        <p className="max-w-md text-sm text-muted-foreground">{serviceExplanation(service)}</p>
      </div>
    )
  }

  /**
   * **The hours notice is deliberately NOT drawn here - §9.328.**
   *
   * The owner: *"When Ii enable Otváracie hodiny -> Oznam o hodinách, in customer website I see it at
   * the top of the whole page and after category selector... it should not be visible at the top of
   * the page. Remove it from there"*.
   *
   * They were seeing it **twice**, because two readers of the same `announcement` field on
   * `/settings/opening-hours` had each grown a renderer: `hoursNotice` here, above the router, and
   * `announcementOf` / `announcementShows` on `HomePage`. The logic is identical in both - enabled,
   * non-empty, `showAlways || !isOpen` - so an enabled notice rendered once at the very top and again
   * below the closed bar.
   *
   * `HomePage` is the copy that stays, and that is parity rather than preference: legacy draws this
   * banner in `Home.tsx` and nowhere else. Keeping the gate's copy would have put it on `/checkout`
   * and every CMS page too, which legacy never did.
   *
   * **The general notice stays here**, because it is a different setting from a different endpoint
   * (`/settings/general`) and this is its only reader - the standing message, which does belong on
   * every route.
   */
  const standing = generalNotice(general)
  const notices = standing === null ? [] : [standing]

  return (
    <>
      {notices.length > 0 && (
        <div data-customer-notices={notices.length}>
          {notices.map((notice) => (
            <div
              key={notice.kind}
              data-customer-notice={notice.kind}
              className="flex items-start gap-2 px-4 py-2 bg-amber-500/15 border-b border-amber-500/30 text-xs text-amber-700 dark:text-amber-300"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
              <span className="min-w-0">{notice.message}</span>
            </div>
          ))}
        </div>
      )}
      {children}
    </>
  )
}
